import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SiteHeader } from "@/components/Logo";
import { Mail, CheckCircle2, ArrowLeft, KeyRound } from "lucide-react";
import { requestMagicLink, verifyLoginCode, setAuth } from "@/lib/authApi";
import useAuth from "@/hooks/useAuth";

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const { isSignedIn } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    // Already signed in — bounce to the intended destination
    if (isSignedIn) nav(next, { replace: true });
  }, [isSignedIn, next, nav]);

  if (isSignedIn) return null;

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await requestMagicLink(clean);
      setSent(true);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || "Couldn't send the link. Try again in a minute.");
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error("Enter the 6-digit code from the email");
      return;
    }
    setVerifying(true);
    try {
      const { session_token, user } = await verifyLoginCode(
        email.trim().toLowerCase(),
        code
      );
      setAuth({ session_token, user });
      toast.success(`Signed in as ${user.email}`);
      nav(next, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That code didn't work.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-pbo-paper">
      <SiteHeader />
      <div className="max-w-md mx-auto px-6 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-pbo-muted hover:text-pbo-green mb-6"
          data-testid="login-back-link"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to home
        </Link>

        <div className="bg-white border border-pbo-border rounded-2xl p-8 shadow-sm">
          {sent ? (
            <div data-testid="login-sent-view">
              <div className="w-14 h-14 rounded-full bg-pbo-green/10 text-pbo-green grid place-items-center mb-4">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h1 className="font-serif text-pbo-green text-3xl mb-2">
                Check your email
              </h1>
              <p className="text-pbo-ink/80 text-sm mb-5 leading-relaxed">
                We just sent a sign-in link to{" "}
                <strong className="text-pbo-green">{email}</strong>. Tap the
                button in the email, or enter the 6-digit code below — the code
                works everywhere, including the home-screen app.
              </p>

              <form
                onSubmit={submitCode}
                className="mb-5"
                data-testid="login-code-form"
              >
                <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold text-pbo-muted mb-1.5">
                  <KeyRound className="w-3 h-3 inline mr-1 -mt-0.5" />
                  6-digit code from the email
                </label>
                <div className="flex gap-2">
                  <Input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="bg-white border-pbo-border h-11 font-mono text-xl tracking-[0.3em] text-center flex-1"
                    data-testid="login-code-input"
                  />
                  <Button
                    type="submit"
                    disabled={verifying || code.length !== 6}
                    className="bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream h-11 px-5 shrink-0"
                    data-testid="login-code-submit-btn"
                  >
                    {verifying ? "…" : "Sign in"}
                  </Button>
                </div>
              </form>

              <div className="text-xs text-pbo-muted bg-pbo-paper border border-pbo-border rounded-md p-3 mb-4">
                Didn&apos;t get it? Check spam, or wait a minute and try again
                (up to 3 requests per 10 min).
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setSent(false);
                  setEmail("");
                }}
                className="border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream w-full"
                data-testid="login-use-different-email-btn"
              >
                Use a different email
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="login-form">
              <div className="w-14 h-14 rounded-full bg-pbo-green/10 text-pbo-green grid place-items-center mb-4">
                <Mail className="w-7 h-7" />
              </div>
              <h1 className="font-serif text-pbo-green text-3xl mb-2">
                Sign in
              </h1>
              <p className="text-pbo-ink/70 text-sm mb-6 leading-relaxed">
                Enter your email. We&apos;ll send you a one-tap sign-in link —
                no passwords to remember.
              </p>

              <label className="block text-[10px] uppercase tracking-[0.2em] font-semibold text-pbo-muted mb-1.5">
                Your email
              </label>
              <Input
                type="email"
                required
                autoFocus
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mb-5 bg-white border-pbo-border h-11"
                data-testid="login-email-input"
              />

              <Button
                type="submit"
                disabled={loading}
                className="bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream w-full h-11"
                data-testid="login-submit-btn"
              >
                {loading ? "Sending…" : "Send my sign-in link"}
              </Button>

              <p className="text-[11px] text-pbo-muted mt-4 leading-relaxed">
                First time signing in? A new account is created automatically —
                no separate signup step.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
